/**
 * Pill Button Component
 *
 * A button component with pill (rounded-full) styling, typically used for
 * compact status displays, filters, or navigation elements.
 *
 * Features:
 * - Pill shape (rounded-full)
 * - Multiple variants (default, glass, destructive, etc.)
 * - Icon support (left/right)
 * - Disabled state
 * - Full accessibility support
 *
 * @example
 * ```tsx
 * <PillButton onClick={handleClick} leftIcon={<MapPin />}>
 *   Sweden · SEK
 * </PillButton>
 * ```
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/utils/utils';

const pillButtonVariants = cva(
  [
    // Layout
    'inline-flex items-center gap-2',
    'whitespace-nowrap',
    // Shape - pill style
    'rounded-full',
    // Typography
    'text-body-sm-responsive font-medium',
    // Transitions
    'transition-colors duration-200 ease-out',
    // Focus states
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-primary',
    'focus-visible:ring-offset-2',
    // Disabled state
    'disabled:pointer-events-none',
    'disabled:opacity-50',
    // Touch optimization
    'touch-manipulation',
    'cursor-pointer',
  ].join(' '),
  {
    variants: {
      variant: {
        // Default: Glass effect
        default: [
          'border border-glass-border/60',
          'bg-glass/80 backdrop-blur-sm',
          'text-foreground',
          'hover:bg-card hover:border-border',
          'focus-visible:bg-card focus-visible:border-border',
          'active:bg-muted/50',
        ].join(' '),

        // Glass: Explicit glass variant
        glass: [
          'border border-glass-border/60',
          'bg-glass/80 backdrop-blur-sm',
          'text-foreground',
          'hover:bg-card hover:border-border',
          'focus-visible:bg-card focus-visible:border-border',
          'active:bg-muted/50',
        ].join(' '),

        // Loading: Muted variant for loading states
        loading: [
          'border border-glass-border/60',
          'bg-glass/80 backdrop-blur-sm',
          'text-muted-foreground',
        ].join(' '),

        // Destructive: Error states
        destructive: [
          'border border-destructive/40',
          'bg-transparent',
          'text-destructive',
          'hover:bg-destructive/5',
          'focus-visible:bg-destructive/5',
          'active:bg-destructive/5',
        ].join(' '),
      },
      size: {
        sm: 'px-2.5 py-1 text-caption',
        md: 'px-3 py-1.5',
        lg: 'px-4 py-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface PillButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof pillButtonVariants> {
  /** Icon to display on the left */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right */
  rightIcon?: React.ReactNode;
  /** Enable text truncation for long content */
  truncate?: boolean;
}

export const PillButton = React.forwardRef<HTMLButtonElement, PillButtonProps>(
  function PillButton(
    { className, variant, size, leftIcon, rightIcon, truncate = false, children, ...props },
    ref
  ) {
    const content = truncate ? (
      <span className="truncate max-w-truncate-sm">{children}</span>
    ) : (
      children
    );

    return (
      <button
        ref={ref}
        type="button"
        className={cn(pillButtonVariants({ variant, size }), className)}
        {...props}
      >
        {leftIcon && (
          <span className="inline-flex items-center" aria-hidden="true">
            {leftIcon}
          </span>
        )}
        {content}
        {rightIcon && (
          <span className="inline-flex items-center" aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);
